import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LogOut, Settings, FileText, Image as ImageIcon, 
  Mail, TrendingUp, Users, Activity, Key, FolderOpen, DollarSign, Video, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api, { investorsAPI, documentsAPI, imagesAPI } from '../services/api';
import APIKeysSettings from '../components/admin/APIKeysSettings';
import ActivityLogs from '../components/admin/ActivityLogs';
import InvestorManagement from '../components/admin/InvestorManagement';
import ContactManagement from '../components/admin/ContactManagement';
import ProjectManagement from '../components/admin/ProjectManagement';
import TeamManagement from '../components/admin/TeamManagement';
import DocumentManagement from '../components/admin/DocumentManagement';
import MediaManagement from '../components/admin/MediaManagement';
import FinancialManagement from '../components/admin/FinancialManagement';
import VideoManagement from '../components/admin/VideoManagement';
import AccountSettings from '../components/admin/AccountSettings';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    inquiries: 0,
    documents: 0,
    images: 0,
    activities: 0,
  });
  const [recentActivities, setRecentActivities] = useState([]);

  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('token');
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (!token || !isAdmin) {
      toast.error('Accès non autorisé');
      navigate('/');
    }
  }, [navigate]);

  const loadStats = useCallback(async () => {
    try {
      const [inquiriesRes, documentsRes, imagesRes, activitiesRes] = await Promise.all([
        investorsAPI.getAll(),
        documentsAPI.getAll(),
        imagesAPI.getAll(),
        api.get('/activity-logs'),
      ]);
      setStats({
        inquiries: Array.isArray(inquiriesRes.data) ? inquiriesRes.data.length : 0,
        documents: Array.isArray(documentsRes.data) ? documentsRes.data.length : 0,
        images: Array.isArray(imagesRes.data) ? imagesRes.data.length : 0,
        activities: Array.isArray(activitiesRes.data) ? activitiesRes.data.length : 0,
      });
      const list = Array.isArray(activitiesRes.data) ? activitiesRes.data.slice(0, 5) : [];
      setRecentActivities(list);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    loadStats();
  }, [checkAuth, loadStats]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('user');
    toast.success('Déconnecté avec succès');
    navigate('/');
  };



  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: TrendingUp },
    { id: 'project', label: 'Projet', icon: FolderOpen },
    { id: 'video', label: 'Vidéo YouTube', icon: Video },
    { id: 'team', label: 'Équipe & Partenaires', icon: Users },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'media', label: 'Médias', icon: ImageIcon },
    { id: 'financial', label: 'Financier', icon: DollarSign },
    { id: 'inquiries', label: 'Demandes Investisseurs', icon: Mail },
    { id: 'contacts', label: 'Messages Contact', icon: MessageSquare },
    { id: 'account', label: 'Paramètres', icon: Settings },
    { id: 'api-keys', label: 'Clés API', icon: Key },
    { id: 'logs', label: 'Logs d\'Activité', icon: Activity },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="min-h-screen bg-black/20">
        {/* Header */}
        <header className="bg-slate-800/50 border-b border-slate-700 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-4">
                <Settings className="w-8 h-8 text-blue-400" />
                <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="text-red-400 hover:text-red-300"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Tabs Navigation */}
        <div className="bg-slate-800/30 border-b border-slate-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-1 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-400 text-blue-400'
                        : 'border-transparent text-gray-400 hover:text-gray-300'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-fadeIn">
              <StatCard
                title="Demandes Investisseurs"
                value={stats.inquiries}
                icon={Mail}
                color="blue"
              />
              <StatCard
                title="Documents"
                value={stats.documents}
                icon={FileText}
                color="green"
              />
              <StatCard
                title="Images Galerie"
                value={stats.images}
                icon={ImageIcon}
                color="purple"
              />
              <StatCard
                title="Activités Récentes"
                value={stats.activities}
                icon={Activity}
                color="orange"
              />
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="mt-8 glass rounded-xl p-6 border border-slate-700 animate-fadeIn">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-semibold text-lg">Activités Récentes</h2>
                <button
                  onClick={() => setActiveTab('logs')}
                  className="text-blue-400 hover:text-blue-300"
                >
                  Voir toutes
                </button>
              </div>
              <ul className="space-y-3">
                {recentActivities.length === 0 && (
                  <li className="text-gray-400">Aucune activité récente</li>
                )}
                {recentActivities.map((item) => (
                  <li key={item.id} className="flex items-center justify-between bg-slate-800/40 rounded-lg px-4 py-3">
                    <div className="text-gray-200">
                      <span className="font-medium">{item.action}</span>
                      {item.details ? <span className="text-gray-400"> — {item.details}</span> : null}
                    </div>
                    <div className="text-gray-400 text-sm">{new Date(item.timestamp).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === 'project' && <ProjectManagement />}
          {activeTab === 'video' && <VideoManagement />}
          {activeTab === 'team' && <TeamManagement />}
          {activeTab === 'documents' && <DocumentManagement />}
          {activeTab === 'media' && <MediaManagement />}
          {activeTab === 'financial' && <FinancialManagement />}
          {activeTab === 'inquiries' && <InvestorManagement />}
          {activeTab === 'contacts' && <ContactManagement />}
          {activeTab === 'account' && <AccountSettings />}
          {activeTab === 'api-keys' && <APIKeysSettings />}
          {activeTab === 'logs' && <ActivityLogs />}
        </main>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  };

  return (
    <div className={`glass rounded-xl p-6 border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm">{title}</p>
          <p className="text-3xl font-bold mt-2" data-testid="stat-value">{value}</p>
        </div>
        <Icon className="w-12 h-12 opacity-50" />
      </div>
    </div>
  );
};

export default AdminDashboard;
